import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Clock, CheckCircle, Loader2, ListOrdered, ChevronRight, AlertCircle, CalendarPlus } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, isBefore, startOfDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { useClientAuth } from '@/context/ClientAuthContext';
import { ReferencePhotoUploader } from '@/components/ReferencePhotoUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const { getAuthHeaders } = useClientAuth();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistTime, setWaitlistTime] = useState('');
  const [salonPhone, setSalonPhone] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [lastBookingId, setLastBookingId] = useState('');

  // Booking form config
  const [formConfig, setFormConfig] = useState(null);
  const [preferences, setPreferences] = useState({});
  const [prefErrors, setPrefErrors] = useState({});
  const [photos, setPhotos] = useState([]);
  const photoConfig = formConfig?.photo_upload;
  const photoEnabled = !!(formConfig?.enabled && photoConfig?.enabled);
  const uploadedPhotoIds = photos.filter((p) => p.status === 'done' && p.id).map((p) => p.id);

  const [formData, setFormData] = useState({
    service_id: '', service_name: '', date: null, time: '', notes: ''
  });

  const TOTAL_STEPS = formConfig?.enabled && (formConfig?.questions?.length > 0 || photoEnabled) ? 5 : 4;

  useEffect(() => { fetchServices(); fetchSalonPhone(); fetchFormConfig(); }, []);

  useEffect(() => {
    if (formData.date) fetchAvailableSlots(format(formData.date, 'yyyy-MM-dd'));
  }, [formData.date]);

  const fetchServices = async () => {
    try { const r = await axios.get(`${API}/services`); setServices(r.data); }
    catch { toast.error('Errore nel caricamento dei servizi'); }
  };

  const fetchSalonPhone = async () => {
    try { const r = await axios.get(`${API}/site-texts`); setSalonPhone(r.data.salonPhone || ''); }
    catch (e) { console.error('Failed to fetch salon phone:', e); }
  };

  const fetchFormConfig = async () => {
    try { const r = await axios.get(`${API}/booking-form/config`); setFormConfig(r.data); }
    catch (e) { console.error('Failed to fetch form config:', e); }
  };

  const fetchAvailableSlots = async (date) => {
    try { const r = await axios.get(`${API}/bookings/available-slots?date=${date}`); setAvailableSlots(r.data.available_slots); }
    catch { toast.error('Errore nel caricamento degli orari disponibili'); }
  };

  const handleServiceSelect = (service) => {
    setFormData({ ...formData, service_id: service.id, service_name: service.name });
    setValidationErrors(prev => ({ ...prev, service: false }));
  };

  const handleDateSelect = (date) => {
    setFormData({ ...formData, date });
    setValidationErrors(prev => ({ ...prev, date: false }));
  };

  const handleTimeSelect = (time) => {
    setFormData({ ...formData, time: formData.time === time ? '' : time });
    setValidationErrors(prev => ({ ...prev, time: false }));
  };

  const validateAndNext = (currentStep) => {
    const errors = {};
    if (currentStep === 1 && !formData.service_id) {
      errors.service = true; toast.error('Seleziona un servizio per continuare');
    }
    if (currentStep === 2 && !formData.date) {
      errors.date = true; toast.error('Seleziona una data per continuare');
    }
    if (currentStep === 3 && !formData.time) {
      errors.time = true; toast.error('Seleziona un orario per continuare');
    }
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // Step 4 is preferences (if enabled), otherwise jump to confirm
    if (currentStep === 3 && TOTAL_STEPS === 4) {
      setStep(TOTAL_STEPS); // skip preferences, go to confirm
    } else {
      setStep(currentStep + 1);
    }
  };

  const validatePreferences = () => {
    if (!formConfig?.enabled) return true;
    const errors = {};
    for (const q of formConfig.questions) {
      if (q.required && !preferences[q.id]?.trim()) {
        errors[q.id] = true;
      }
      if (q.type === 'text' && q.max_length && preferences[q.id]?.length > q.max_length) {
        errors[q.id] = true;
      }
    }
    setPrefErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Compila tutti i campi obbligatori');
      return false;
    }
    return true;
  };

  const handlePreferencesNext = () => {
    if (photos.some((p) => p.status === 'uploading')) { toast.error('Attendi il termine del caricamento delle foto'); return; }
    if (!validatePreferences()) return;
    setStep(TOTAL_STEPS);
  };

  const buildPreferencesPayload = () => {
    if (!formConfig?.enabled || !formConfig?.questions?.length) return null;
    return formConfig.questions.filter(q => preferences[q.id]?.trim()).map(q => ({
      question_id: q.id,
      question_label: q.label,
      answer: preferences[q.id].trim(),
      type: q.type,
      emoji: q.emoji || ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const bookingData = {
        service_id: formData.service_id, service_name: formData.service_name,
        date: format(formData.date, 'yyyy-MM-dd'), time: formData.time, notes: formData.notes,
        client_preferences: buildPreferencesPayload(),
        reference_photo_ids: photoEnabled ? uploadedPhotoIds : []
      };
      const res = await axios.post(`${API}/bookings`, bookingData, { headers: getAuthHeaders() });
      if (res.data?.id) setLastBookingId(res.data.id);
      setBookingComplete(true);
      toast.success('Prenotazione inviata con successo!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella prenotazione. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinWaitlist = async () => {
    if (!waitlistTime) { toast.error('Scegli un orario preferito'); return; }
    setWaitlistLoading(true);
    try {
      await axios.post(`${API}/client/waitlist`, {
        preferred_date: format(formData.date, 'yyyy-MM-dd'),
        preferred_service: formData.service_name, preferred_time: waitlistTime,
      }, { headers: getAuthHeaders() });
      toast.success("Aggiunto alla lista d'attesa!");
      setStep(1); setFormData({ service_id: '', service_name: '', date: null, time: '', notes: '' });
    } catch (error) { toast.error(error.response?.data?.detail || 'Errore'); }
    finally { setWaitlistLoading(false); }
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    const today = startOfDay(new Date());
    const dow = date.getDay();
    return isBefore(startOfDay(date), today) || dow === 0 || dow === 1;
  };

  const getCalendarAction = (bookingId, serviceName, dateObj, timeStr) => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) return { url: `${API}/bookings/${bookingId}/calendar` };
    if (dateObj && timeStr) {
      const ds = format(dateObj, 'yyyyMMdd');
      const [h, m] = timeStr.split(':');
      const s = `${ds}T${h}${m}00`;
      const eH = String(Number(h) + (Number(m) + 30 >= 60 ? 1 : 0)).padStart(2, '0');
      const eM = String((Number(m) + 30) % 60).padStart(2, '0');
      const e = `${ds}T${eH}${eM}00`;
      return { url: `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent('Appuntamento - CAPELLI & CAPELLI')}&dates=${s}/${e}&ctz=Europe/Rome&details=${encodeURIComponent(`Servizio: ${serviceName}`)}&sf=true&output=xml` };
    }
    return { url: `${API}/bookings/${bookingId}/calendar` };
  };

  // === BOOKING COMPLETE ===
  if (bookingComplete) {
    const calAction = getCalendarAction(lastBookingId, formData.service_name, formData.date, formData.time);
    return (
      <div className="min-h-[80vh] flex items-center justify-center container mx-auto px-4 py-20">
        <Card className="max-w-2xl w-full card-elegant">
          <CardContent className="p-8 sm:p-12 text-center">
            <div className="inline-flex p-4 rounded-full bg-accent/10 mb-6"><CheckCircle className="h-16 w-16 text-accent" /></div>
            <h2 className="text-3xl font-bold mb-4">Prenotazione Ricevuta!</h2>
            <div className="bg-secondary p-6 rounded-lg mb-6 text-left space-y-2">
              <p><span className="font-semibold">Servizio:</span> {formData.service_name}</p>
              <p><span className="font-semibold">Data:</span> {format(formData.date, 'dd MMMM yyyy', { locale: it })}</p>
              <p><span className="font-semibold">Orario:</span> {formData.time}</p>
              {buildPreferencesPayload()?.length > 0 && (
                <div className="pt-2 mt-2 border-t border-border">
                  <p className="font-semibold text-sm mb-1">Le tue preferenze:</p>
                  {buildPreferencesPayload().map(p => (
                    <p key={p.question_id} className="text-sm text-muted-foreground">{p.question_label}: <span className="text-foreground">{p.answer}</span></p>
                  ))}
                </div>
              )}
              {uploadedPhotoIds.length > 0 && (
                <p className="text-sm pt-2" data-testid="success-photo-count"><span className="font-semibold">Foto di riferimento:</span> {uploadedPhotoIds.length} allegat{uploadedPhotoIds.length === 1 ? 'a' : 'e'}</p>
              )}
            </div>
            <a href={calAction.url} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 w-full py-4 px-6 mb-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold text-lg transition-colors animate-pulse"
              data-testid="calendar-add-btn">
              <CalendarPlus className="h-6 w-6" /> Aggiungi al mio calendario
            </a>
            <p className="text-muted-foreground text-sm mb-6">Tocca per salvare l'appuntamento nel tuo calendario</p>
            <Button onClick={() => { setBookingComplete(false); setStep(1); setFormData({ service_id: '', service_name: '', date: null, time: '', notes: '' }); setPreferences({}); setLastBookingId(''); }}
              variant="outline" className="w-full sm:w-auto">Nuova Prenotazione</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // === STEPS ===
  return (
    <div className="min-h-screen py-20 container mx-auto px-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Prenota il Tuo Appuntamento</h1>
          <p className="text-muted-foreground text-lg">Semplice, veloce e disponibile 24/7</p>
        </div>

        {/* Progress */}
        <div className="flex justify-center mb-12">
          <div className="flex items-center gap-2 sm:gap-4">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((s) => (
              <React.Fragment key={s}>
                <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${step >= s ? 'bg-accent text-accent-foreground' : 'bg-secondary text-muted-foreground'}`}>{s}</div>
                {s < TOTAL_STEPS && <div className={`w-5 sm:w-16 h-1 transition-colors ${step > s ? 'bg-accent' : 'bg-secondary'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step 1: Service */}
        {step === 1 && (
          <Card className="card-elegant">
            <CardHeader>
              <CardTitle className="text-2xl">1. Scegli il Servizio <span className="text-destructive">*</span></CardTitle>
              {validationErrors.service && <p className="text-destructive text-sm flex items-center gap-1 mt-1"><AlertCircle className="h-3.5 w-3.5" /> Seleziona un servizio per continuare</p>}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {services.map((service) => (
                  <Card key={service.id} className={`cursor-pointer transition-all hover:shadow-lg ${formData.service_id === service.id ? 'border-accent bg-accent/5 ring-2 ring-accent' : validationErrors.service ? 'border-destructive/50' : 'hover:border-accent'}`}
                    onClick={() => handleServiceSelect(service)} data-testid={`service-card-${service.id}`}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-semibold text-lg">{service.name}</h3>
                        <p className="text-accent font-bold text-xl">€{service.price}</p>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{service.description}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground"><Clock className="h-4 w-4" /><span>{service.duration} minuti</span></div>
                      {formData.service_id === service.id && <div className="mt-3 text-accent text-sm font-semibold flex items-center gap-1"><CheckCircle className="h-4 w-4" /> Selezionato</div>}
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <Button onClick={() => validateAndNext(1)} className="bg-accent text-accent-foreground hover:bg-accent/90 px-8" data-testid="step1-next-btn">Avanti <ChevronRight className="h-4 w-4 ml-1" /></Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Date */}
        {step === 2 && (
          <Card className="card-elegant">
            <CardHeader>
              <CardTitle className="text-2xl">2. Scegli la Data <span className="text-destructive">*</span></CardTitle>
              <p className="text-muted-foreground">Chiuso: Lunedì e Domenica</p>
              {validationErrors.date && <p className="text-destructive text-sm flex items-center gap-1 mt-1"><AlertCircle className="h-3.5 w-3.5" /> Seleziona una data</p>}
            </CardHeader>
            <CardContent>
              <div className="flex justify-center">
                <Calendar mode="single" selected={formData.date} onSelect={handleDateSelect} disabled={isDateDisabled}
                  locale={it} className="rounded-lg border" fromDate={new Date()} toDate={addDays(new Date(), 60)} defaultMonth={new Date()} />
              </div>
              {formData.date && <p className="text-center mt-3 text-sm text-accent font-semibold"><CheckCircle className="h-4 w-4 inline mr-1" /> {format(formData.date, 'dd MMMM yyyy', { locale: it })}</p>}
              <div className="mt-6 flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)}>Indietro</Button>
                <Button onClick={() => validateAndNext(2)} className="bg-accent text-accent-foreground hover:bg-accent/90 px-8" data-testid="step2-next-btn">Avanti <ChevronRight className="h-4 w-4 ml-1" /></Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Time */}
        {step === 3 && (
          <Card className="card-elegant">
            <CardHeader>
              <CardTitle className="text-2xl">3. Scegli l'Orario <span className="text-destructive">*</span></CardTitle>
              <p className="text-muted-foreground">Orari disponibili per {format(formData.date, 'dd MMMM yyyy', { locale: it })}</p>
              {validationErrors.time && <p className="text-destructive text-sm flex items-center gap-1 mt-1"><AlertCircle className="h-3.5 w-3.5" /> Seleziona un orario</p>}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {availableSlots.length === 0 ? (
                  <div className="col-span-full text-center py-6 space-y-5">
                    <p className="text-muted-foreground text-lg">Nessun orario disponibile per questa data</p>
                    <div className="bg-secondary p-5 rounded-lg max-w-md mx-auto space-y-4">
                      <ListOrdered className="h-8 w-8 text-accent mx-auto" />
                      <p className="font-semibold">Mettiti in Lista d'Attesa</p>
                      <div>
                        <label className="text-sm font-medium block mb-1.5 text-left">Orario preferito</label>
                        <input type="time" value={waitlistTime} onChange={(e) => setWaitlistTime(e.target.value)}
                          className="w-full rounded-md border border-border bg-background px-3 py-2 text-base" data-testid="waitlist-time-input" />
                      </div>
                      <Button onClick={handleJoinWaitlist} disabled={waitlistLoading} className="bg-accent text-accent-foreground hover:bg-accent/90 w-full" data-testid="join-waitlist-btn">
                        {waitlistLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ListOrdered className="mr-2 h-4 w-4" />} Lista d'Attesa
                      </Button>
                    </div>
                  </div>
                ) : availableSlots.map((slot) => (
                  <Button key={slot} variant="outline" onClick={() => handleTimeSelect(slot)}
                    className={`transition-all ${formData.time === slot ? 'bg-accent text-accent-foreground border-accent ring-2 ring-accent' : validationErrors.time ? 'border-destructive/50 hover:bg-accent hover:text-accent-foreground' : 'hover:bg-accent hover:text-accent-foreground hover:border-accent'}`}
                    data-testid={`time-slot-${slot}`}>{slot}</Button>
                ))}
              </div>
              {availableSlots.length > 0 && (
                <div className="mt-6 flex justify-between">
                  <Button variant="outline" onClick={() => setStep(2)}>Indietro</Button>
                  <Button onClick={() => validateAndNext(3)} className="bg-accent text-accent-foreground hover:bg-accent/90 px-8" data-testid="step3-next-btn">Avanti <ChevronRight className="h-4 w-4 ml-1" /></Button>
                </div>
              )}
              {availableSlots.length === 0 && <div className="mt-6 text-center"><Button variant="outline" onClick={() => setStep(2)}>Indietro</Button></div>}
            </CardContent>
          </Card>
        )}

        {/* Step 4: Preferences (only if form is enabled) */}
        {step === 4 && TOTAL_STEPS === 5 && formConfig?.enabled && (
          <Card className="card-elegant">
            <CardHeader>
              <CardTitle className="text-2xl" data-testid="pref-form-title">4. {formConfig.emoji ? `${formConfig.emoji} ` : ''}{formConfig.title}</CardTitle>
              <p className="text-muted-foreground">{formConfig.description}</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {formConfig.questions.map((q) => (
                <div key={q.id} className="space-y-2" data-testid={`pref-question-${q.id}`}>
                  <Label className={`text-base font-semibold ${prefErrors[q.id] ? 'text-destructive' : ''}`}>
                    {q.emoji ? `${q.emoji} ` : ''}{q.label} {q.required && <span className="text-destructive">*</span>}
                  </Label>

                  {q.type === 'single_choice' && (
                    <RadioGroup value={preferences[q.id] || ''} onValueChange={(val) => {
                      setPreferences(prev => ({ ...prev, [q.id]: val }));
                      setPrefErrors(prev => ({ ...prev, [q.id]: false }));
                    }}>
                      <div className="space-y-2">
                        {q.options.map((opt) => (
                          <label key={opt.id} data-testid={`pref-option-${q.id}-${opt.id}`}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              preferences[q.id] === opt.label ? 'border-accent bg-accent/5' : prefErrors[q.id] ? 'border-destructive/50' : 'border-border hover:border-accent/50'
                            }`}>
                            <RadioGroupItem value={opt.label} />
                            <span className="text-sm">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                    </RadioGroup>
                  )}

                  {q.type === 'text' && (
                    <div>
                      <Textarea
                        value={preferences[q.id] || ''}
                        onChange={(e) => {
                          const val = e.target.value.slice(0, q.max_length || 500);
                          setPreferences(prev => ({ ...prev, [q.id]: val }));
                          setPrefErrors(prev => ({ ...prev, [q.id]: false }));
                        }}
                        placeholder={q.placeholder || ''}
                        rows={3}
                        className={prefErrors[q.id] ? 'border-destructive' : ''}
                        data-testid={`pref-text-${q.id}`}
                      />
                      <div className="flex justify-between mt-1">
                        {q.hint && <p className="text-xs text-muted-foreground">{q.hint}</p>}
                        <p className="text-xs text-muted-foreground ml-auto">{(preferences[q.id] || '').length}/{q.max_length || 500}</p>
                      </div>
                    </div>
                  )}

                  {prefErrors[q.id] && q.required && (
                    <p className="text-destructive text-xs flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Campo obbligatorio</p>
                  )}
                </div>
              ))}

              {photoEnabled && (
                <ReferencePhotoUploader config={photoConfig} photos={photos} setPhotos={setPhotos} getAuthHeaders={getAuthHeaders} />
              )}

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(3)}>Indietro</Button>
                <Button onClick={handlePreferencesNext} className="bg-accent text-accent-foreground hover:bg-accent/90 px-8" data-testid="step4-next-btn">
                  Avanti <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Final Step: Confirm */}
        {step === TOTAL_STEPS && (
          <Card className="card-elegant">
            <CardHeader>
              <CardTitle className="text-2xl">{TOTAL_STEPS}. Conferma Prenotazione</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-secondary p-6 rounded-lg space-y-3">
                  <h3 className="font-semibold text-lg mb-4">Riepilogo Prenotazione</h3>
                  <p className="text-sm"><span className="font-semibold">Servizio:</span> {formData.service_name}</p>
                  <p className="text-sm"><span className="font-semibold">Data:</span> {format(formData.date, 'dd MMMM yyyy', { locale: it })}</p>
                  <p className="text-sm"><span className="font-semibold">Orario:</span> {formData.time}</p>
                  {buildPreferencesPayload()?.length > 0 && (
                    <div className="pt-3 mt-3 border-t border-border space-y-1.5">
                      <p className="font-semibold text-sm">Le tue preferenze:</p>
                      {buildPreferencesPayload().map(p => (
                        <p key={p.question_id} className="text-sm text-muted-foreground">{p.question_label}: <span className="text-foreground font-medium">{p.answer}</span></p>
                      ))}
                    </div>
                  )}
                  {photoEnabled && uploadedPhotoIds.length > 0 && (
                    <p className="text-sm" data-testid="summary-photo-count"><span className="font-semibold">Foto di riferimento:</span> {uploadedPhotoIds.length} allegat{uploadedPhotoIds.length === 1 ? 'a' : 'e'}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="notes">Note (Opzionale)</Label>
                  <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Eventuali richieste particolari..." rows={3} />
                </div>
                <div className="flex gap-4">
                  <Button type="button" variant="outline" onClick={() => setStep(TOTAL_STEPS === 5 ? 4 : 3)} className="flex-1">Indietro</Button>
                  <Button type="submit" disabled={loading} className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="confirm-booking-btn">
                    {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Invio...</> : 'Conferma Prenotazione'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BookingPage;
